import React, { useState, useEffect } from 'react';
import axios from 'axios';
import styles from './RightSection.module.css';
import ViewMoreModal from '@/components/ViewMoreModal';
import { API_BASE_URL } from '@/config/api';
import { isOwnerRole } from '@/utils/roles';

/**
 * RightSection Component
 * 
 * Display matched bets. Optionally fetches its own data if fetchOpenBetsUrl is provided.
 */
const RightSection = ({
  data = [],
  sectionKey = '',
  title = "Matched",
  viewMoreText = "View More",
  onViewMore,
  fetchOpenBetsUrl = '/lords/bet-list/casino/open-downline', // Default to Casino Downline for LordGames
  fetchAllBetsUrl = '/lords/bet-list/casino/all-downline',   // Default to Casino Downline for LordGames
  columns = [
    { key: 'userName', label: 'UserName', minWidth: null },
    { key: 'nation', label: 'Nation', minWidth: '140px' },
    { key: 'userrate', label: 'Userrate', minWidth: '50px' },
    { key: 'amount', label: 'Amount', minWidth: null },
    { key: 'placeDate', label: 'PlaceDate', minWidth: '80px' },
    { key: 'matchDate', label: 'MatchDate', minWidth: '80px' },
    { key: 'gametype', label: 'Gametype', minWidth: '60px' },
  ],
  renderCell,
  emptyMessage = "There are no records to show",
  className = '',
}) => {
  const [showModal, setShowModal] = useState(false);
  const [displayData, setDisplayData] = useState(data);

  // Sync displayData with prop data if no internal fetch is used
  useEffect(() => {
    if (!fetchOpenBetsUrl) {
      setDisplayData(data);
    }
  }, [data, fetchOpenBetsUrl]);

  // Fetch Open Bets if URL provided
  useEffect(() => {
    let interval;
    const fetchData = async () => {
      if (!fetchOpenBetsUrl) return;
      try {
        // Correctly identify token based on active role
        const activeRole = localStorage.getItem('activeRole');
        let token = localStorage.getItem('token');
        if (isOwnerRole(activeRole)) {
            token = localStorage.getItem('ownerToken');
        } else if (activeRole === 'staff') {
            token = localStorage.getItem('staffToken');
        }

        const url = `${API_BASE_URL}${fetchOpenBetsUrl}`;
        // Append sectionKey as gameName if present
        const finalUrl = sectionKey ? `${url}?gameName=${sectionKey}` : url;
        
        const res = await axios.get(finalUrl, {
           headers: { Authorization: `Bearer ${token}` }
        });
        if (res.data && res.data.success) {
            setDisplayData(res.data.data || []);
        }
      } catch (err) {
        console.error("Error fetching RightSection data:", err);
      }
    };

    if (fetchOpenBetsUrl) {
        fetchData();
        interval = setInterval(fetchData, 5000); // Auto-refresh same as Sports
    }
    return () => clearInterval(interval);
  }, [fetchOpenBetsUrl, sectionKey]);

  const handleViewMore = () => {
    setShowModal(true);
    if (onViewMore) {
      onViewMore();
    }
  };



  const handleCloseModal = () => {
    setShowModal(false);
  };

  const defaultRenderCell = (bet, columnKey, index) => {
    // Default cell rendering logic
    switch (columnKey) {
      case 'userName':
        return bet.username || bet.userName || 'You';
      case 'nation':
        return bet.matchedBet || bet.nation || bet.description || '-';
      case 'userrate':
        return bet.odds || bet.userrate || '-';
      case 'amount':
        return bet.stake || bet.amount || '-';
      case 'placeDate':
        return bet.placeDate || bet.created_at || '-';
      case 'matchDate':
        return bet.matchDate || '-';
      case 'gametype':
        return bet.gametype || bet.game_type || 'Back/Lay';
      default:
        return bet[columnKey] || '-';
    }
  };

  const sectionId = sectionKey ? `right-section-${sectionKey}` : 'right-section';
  const matchedBetId = sectionKey ? `matched-bet-${sectionKey}` : 'matched-bet';
  const modalTitle = "View More";

  const renderTable = () => (
    <div className={styles.tableContainer}>
      <table className={`table m-b-0 ${styles.couponTable}`}>
        <thead>
          <tr className={styles.tableHeader}>
            {columns.map((column, index) => (
              <th
                key={index}
                style={{
                  color: "#fff",
                  minWidth: column.minWidth || undefined,
                  ...column.style,
                }}
              >
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody style={{ color: "#fff", backgroundColor: "transparent" }}>
          {displayData.length > 0 ? (
            displayData.map((bet, idx) => {
                // Determine row style. Default to Back (Blue) if unknown.
                // If bet.type or bet.game_type contains 'lay' -> layRow
                // Else -> backRow
                // Note: 'gametype' from DB is often just the game code (e.g. teen20c), so we might need better logic.
                // Assuming default is Back.
                const isLay = (bet.gametype && bet.gametype.toLowerCase().includes('lay')) || 
                              (bet.modetype && bet.modetype.toLowerCase() === 'lay');
                const rowClass = isLay ? styles.layRow : styles.backRow;
                
                return (
                  <tr key={idx} className={rowClass}>
                    {columns.map((column, colIndex) => (
                      <td
                        key={colIndex}
                        style={{
                          // Remove color: #fff to let row color persist, unless specific override needed
                         // color: "#fff", 
                          minWidth: column.minWidth || undefined,
                          ...column.cellStyle,
                        }}
                      >
                        {renderCell
                          ? renderCell(bet, column.key, idx)
                          : defaultRenderCell(bet, column.key, idx)}
                      </td>
                    ))}
                  </tr>
                );
            })
          ) : (
            <tr style={{ color: "#fff", backgroundColor: "transparent" }}>
              <td colSpan={columns.length} className={styles.noRecords}>
                {emptyMessage}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );

  return (
    <>
      <div className={`${styles.placeBet} ${className}`} data-section-key={sectionKey}>
      <div>
        <div className={styles.header}>
          <div className={styles.navTitle}>
            <ul className="nav nav-tabs" style={{ border: 0 }}>
              <li className="nav-item d-inline-block">
                <div className={`nav-link active ${styles.matchedBetTab}`} style={{ borderRadius: 0 }}>
                  {title} ({displayData.length})
                </div>
              </li>
            </ul>
          </div>
          <div>
            {onViewMore ? (
              <a className={styles.viewMoreBtn} onClick={handleViewMore} data-section-key={sectionKey}>
                {viewMoreText}
              </a>
            ) : (
              <a className={styles.viewMoreBtn} data-section-key={sectionKey}>{viewMoreText}</a>
            )}
          </div>
        </div>

        <div className="tab-content">
          <div id={matchedBetId} className="tab-pane active">
            {renderTable()}
          </div>
        </div>
      </div>
    </div>

      <ViewMoreModal
        show={showModal}
        onHide={handleCloseModal}
        title={modalTitle}
        sectionKey={sectionKey}
        data={displayData}
        fetchUrl={fetchAllBetsUrl} // Pass URL for full history
      />
    </>
  );
};

export default RightSection;

